import { InputNumber, message } from 'antd';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { convertCoinsToPrice, convertPriceToCoins } from '../../utils/currency';

const PriceInput = styled(InputNumber)`
  width: 80px;
`;

interface PriceInputCellProps {
  /** Current price in coins. */
  value: number | null;
  /** Old price may be cleared; the current price may not. */
  allowEmpty?: boolean;
  /** Returns an error message when the new price (in coins) can't be saved. */
  validate?: (coins: number | null) => string | undefined;
  /** Receives the new price in coins (null when cleared). */
  onSave: (coins: number | null) => Promise<unknown>;
}

const PRICE_INPUT_ATTR = 'data-price-input';

/** Moves focus to the previous/next price input in the table; false at either end. */
const focusSiblingPriceInput = (current: HTMLInputElement, step: 1 | -1): boolean => {
  const inputs = Array.from(
    document.querySelectorAll<HTMLInputElement>(`input[${PRICE_INPUT_ATTR}]`),
  );
  const next = inputs[inputs.indexOf(current) + step];
  if (!next) return false;
  next.focus();
  return true;
};

/**
 * Inline price editor for the products table. Saves on blur or Enter, reverts on Escape
 * or when the save fails. Tab / Shift+Tab jump between price inputs only, skipping the
 * links and action buttons in between.
 */
export const PriceInputCell: React.FC<PriceInputCellProps> = ({
  value,
  allowEmpty,
  validate,
  onSave,
}) => {
  const [draft, setDraft] = useState<number | null>(convertCoinsToPrice(value));

  // Pick up the fresh value after a refetch (or another edit of the same row).
  useEffect(() => {
    setDraft(convertCoinsToPrice(value));
  }, [value]);

  const revert = () => setDraft(convertCoinsToPrice(value));

  const commit = async () => {
    if (draft === null && !allowEmpty) return revert();

    const coins = draft === null ? null : convertPriceToCoins(draft);
    if (coins === value) return;

    const error = validate?.(coins);
    if (error) {
      message.error(error);
      return revert();
    }

    try {
      await onSave(coins);
    } catch {
      revert();
    }
  };

  return (
    <PriceInput
      size="small"
      min={0}
      precision={2}
      value={draft}
      {...{ [PRICE_INPUT_ATTR]: true }}
      onChange={(next) => setDraft(next as number | null)}
      onBlur={commit}
      onFocus={(e) => e.target.select()}
      onPressEnter={(e) => e.currentTarget.blur()}
      onKeyDown={(e) => {
        if (e.key === 'Tab') {
          if (focusSiblingPriceInput(e.currentTarget, e.shiftKey ? -1 : 1)) e.preventDefault();
        } else if (e.key === 'Escape') {
          revert();
          // Blur after the reverted value lands, so commit() sees no change.
          setTimeout(() => (e.target as HTMLInputElement).blur());
        }
      }}
    />
  );
};
